// Authentication service for API calls
const API_BASE_URL = process.env.REACT_APP_BASE_URL ? `${process.env.REACT_APP_BASE_URL}/api` : '/api';

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  workspace_id: number;
  two_fa_enabled?: boolean;
  last_login?: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface MagicLinkResponse {
  success: boolean;
  message: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  workspaceName: string;
  country?: string;
}

class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    // Load tokens from localStorage on initialization
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  private async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = `${API_BASE_URL}${endpoint}`;
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add authorization header if we have a token
    if (this.accessToken && !endpoint.includes('/auth/password/') && !endpoint.includes('/auth/magic/')) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${this.accessToken}`,
      };
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401 && this.refreshToken) {
          // Try to refresh the token
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            // Retry the original request with new token
            return this.request(endpoint, options);
          } else {
            // Refresh failed, redirect to login
            this.logout();
            throw new Error('Session expired');
          }
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Magic Link Authentication
  async requestMagicLink(email: string): Promise<MagicLinkResponse> {
    return this.request('/auth/magic/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyMagicLink(token: string): Promise<LoginResponse> {
    const response = await this.request(`/auth/magic/verify/${token}`, {
      method: 'GET',
    });

    if (response.success) {
      this.setTokens(response.accessToken, response.refreshToken);
    }

    return response;
  }

  // Password Reset Authentication
  async requestPasswordReset(email: string): Promise<MagicLinkResponse> {
    return this.request('/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyResetToken(token: string): Promise<any> {
    return this.request(`/auth/password/verify-token/${token}`, {
      method: 'GET',
    });
  }

  async resetPassword(token: string, password: string): Promise<any> {
    return this.request('/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // Password Authentication
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.request('/auth/password/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.success) {
      this.setTokens(response.accessToken, response.refreshToken);
    }

    return response;
  }

  async register(data: RegisterData): Promise<any> {
    return this.request('/auth/password/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.request('/auth/session/me');
    return response.user;
  }

  async logout(): Promise<void> {
    try {
      if (this.accessToken) {
        await this.request('/auth/session/logout', {
          method: 'POST',
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    this.clearTokens();
  }

  // Token management
  private setTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  private clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) return false;

    try {
      const response = await this.request('/auth/session/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (response.success) {
        this.setTokens(response.accessToken, response.refreshToken);
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }

    return false;
  }
}

const authService = new AuthService();
export default authService;
